import { Injectable, inject } from '@angular/core'
import * as CryptoJS from 'crypto-js'
import { EnvironmentService } from './environment.service'

@Injectable({
  providedIn: 'root'
})
export class EncryptDecryptService {

  #environmentService: EnvironmentService = inject(EnvironmentService)

  decrypt<T>(data: string, key: string, forceDecrypt = false): T {
    if (!data) return undefined as T

    if (this.#environmentService.getIgnoreEncryption() && !forceDecrypt) {
      try {
        // Try parsing as JSON first
        return JSON.parse(typeof data === 'string' ? data : JSON.stringify(data)) as T;
      } catch {
        // If JSON parsing fails, return the string value directly
        return data as unknown as T;
      }
    }

    try {
      const _key = CryptoJS.enc.Utf8.parse(key)
      const _iv = CryptoJS.enc.Utf8.parse('')

      const decrypted = CryptoJS.AES.decrypt(data!, _key, {
        keySize: 32,
        iv: _iv,
      }).toString(CryptoJS.enc.Utf8)
      
      if (!decrypted) {
        console.warn('Decryption resulted in empty string, returning undefined');
        return undefined as T;
      }
      
      try {
        // Try parsing as JSON
        return JSON.parse(decrypted) as T;
      } catch {
        // If JSON parsing fails, return the decrypted string directly
        return decrypted as unknown as T;
      }
    } catch (error) {
      console.error('Decryption failed with malformed UTF-8 data:', error);
      // Return undefined instead of throwing to prevent app crashes
      return undefined as T;
    }
  }

  encrypt(data: unknown, key: string): string {
    if (!data) return ''

    if (this.#environmentService.getIgnoreEncryption()) {
      return data as string
    }

    try {
      const _key = CryptoJS.enc.Utf8.parse(key)
      const _iv = CryptoJS.enc.Utf8.parse('')
      
      // Ensure data is properly stringified and sanitized
      const stringData = JSON.stringify(data)
      
      return CryptoJS.AES.encrypt(stringData, _key, {
        keySize: 32,
        iv: _iv
      }).toString()
    } catch (error) {
      console.error('Encryption failed:', error);
      // Return empty string instead of throwing
      return ''
    }
  }
}
